import React, {PureComponent} from "react"
import PropTypes from "prop-types"
import {Body, H1, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {TeamPermissionsListItem, TeamState, Team} from "@irati/types"
import teamService from "@irati/identity/services/teams"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import {hangPromise} from "@irati/utils/promise"
import Loading from "@irati/ui/src/Loading"
import Button from "@irati/ui/src/Button"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import Pill from "@irati/ui/src/Pill"
import GenericModal from "@irati/ui/src/GenericModal"
import History from "@irati/utils/history"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import BURegion, {BURegionOnChange} from "@irati-web/fields/team/BURegion"
import {Row, Col} from "@irati/ui/src/Layout"
import buService from "@irati/billing/services/bu"
import adminTracking from "@irati-tracking/admin"
import usersService from "@irati/identity/services/users"
import Selector from "@irati/ui/src/Selector"
import authentication from "@irati/auth"

import ToastBanner from "../../../components/ToastBanner"
import {VIEW_PEOPLE} from "../../../routes/routeConfig"
import {FirstCol, EndCol} from "./styled"
import TeamListItem from "./ListItem"

// eslint-disable-next-line no-undef
const REASONS: ReasonsInterface[] = [
  {
    key: "admin.countries.deleteCountry.notCentralizingGlobalBenefits",
    reason: "notCentralizingGlobalBenefits"
  },
  {
    key: "admin.countries.deleteCountry.noUsersCountry",
    reason: "noUsersCountry"
  },
  {
    key: "admin.countries.deleteCountry.localBenefitsExistOverseas",
    reason: "localBenefitsExistOverseas"
  },
  {
    key: "admin.countries.deleteCountry.contractorsInelligibletoBenefits",
    reason: "contractorsInelligibletoBenefits"
  },
  {
    key: "admin.countries.deleteCountry.notReadytoOfferBenefits",
    reason: "notReadytoOfferBenefits"
  },
  {
    key: "admin.countries.deleteCountry.other",
    reason: "other"
  }
]

interface Props extends InjectIntl, WithAuth {}
interface State {
  billingUnits: Team[]
  rootTeam?: Team
  selectedTeam: TeamPermissionsListItem | null
  load: Promise<any> | null
  teamId?: string
  country?: string
  deleteSubmit: Promise<any> | null
  showSuccessModal: boolean
  showAddBUModal?: boolean
  showTeamContainsUsersModal: boolean
  showDeleteConfirmationModal: boolean
  showReasonsModal: boolean
  showSuccessfullyDeletedModal: boolean
  selectedReasons: string[]
  showToastBanner: boolean
  teamRegion?: string
}

class List extends PureComponent<Props, State> {
  static contextTypes = {
    apps: PropTypes.object
  }

  state: State = {
    billingUnits: [],
    selectedTeam: null,
    load: hangPromise(),
    showTeamContainsUsersModal: false,
    showSuccessModal: false,
    showDeleteConfirmationModal: false,
    showReasonsModal: false,
    showSuccessfullyDeletedModal: false,
    showToastBanner: false,
    selectedReasons: [],
    showAddBUModal: this.props?.location?.state?.showModal
  }

  componentDidMount() {
    const load = this.getTeamsAndPermissions()
    this.setState({load})
  }

  getTeamsAndPermissions = () => {
    const business = authentication.business()
    return officesForTenant(business, true).then((billingUnits) => {
      const teamName = "$$TEAM_ROOT$$"
      return teamService.teamsPaginated(business.uuid, teamName, undefined, undefined, TeamState.Protected).then((res) => {
        this.setState({
          billingUnits,
          rootTeam: res?.teams[0] || undefined
        })
      })
    })
  }

  setSelectedTeam = (businessUnitId: string) => {
    adminTracking.viewRegions()
    History.push({pathname: VIEW_PEOPLE, state: {businessUnitId}}, this.context.apps.ADMIN)
  }

  toggleSuccessModal = (showSuccessModal: boolean) => this.setState({showSuccessModal})

  showAddBU = () => {
    adminTracking.addRegions()
    this.setState({showAddBUModal: true})
  }

  addBUonChange: BURegionOnChange = (teamRegion) => {
    this.setState({teamRegion})
  }

  createBillingUnit = () => {
    const {teamRegion, billingUnits} = this.state
    const business = authentication.business()
    if (teamRegion) {
      buService.createForRegion({businessId: business.uuid, regionId: teamRegion}).then((created) => {
        const newBillingUnits = billingUnits.concat(created.team)
        this.setState({showAddBUModal: false, billingUnits: newBillingUnits})
      })
    }
  }

  deleteTeam = (teamId: string) => {
    adminTracking.clickDeleteCountryIcon()
    usersService.getUsersForBusiness({teamId}, {fetchPolicy: "network-only"}).then(({total}) => {
      if (total > 0) {
        return this.setState({showTeamContainsUsersModal: true})
      }
      const country = this.state.billingUnits?.find(({uuid}) => uuid === teamId)?.teamName.replace("platform.billingUnit.", "")
      return this.setState({showDeleteConfirmationModal: true, teamId, country})
    })
  }

  handleDeleteTeam = () => {
    adminTracking.clickConfirmDelete()
    const {teamId} = this.state
    const deleteSubmit = teamService.delete(authentication.business().uuid, teamId).then(() => {
      return this.getTeamsAndPermissions().then(() => {
        this.setState({showDeleteConfirmationModal: false, showReasonsModal: true, showToastBanner: true})
      })
    })
    this.setState({deleteSubmit})
  }

  handlePillClick = (_, selectedReason: string) => {
    const reasonEvent = REASONS.find((reason) => this.props.formatMessage({id: reason.key}) === selectedReason)?.reason
    if (reasonEvent) {
      const {selectedReasons} = this.state
      let updatedReasons
      if (selectedReasons.find((reason) => reason === reasonEvent)) {
        updatedReasons = selectedReasons.filter((reason) => reason !== reasonEvent)
      } else {
        updatedReasons = [...selectedReasons, reasonEvent]
      }
      this.setState({selectedReasons: updatedReasons})
    }
  }

  handleSubmitReasons = () => {
    const {selectedReasons} = this.state
    adminTracking.submitDeleteCountryReasons(selectedReasons)
    this.setState({showReasonsModal: false, showSuccessfullyDeletedModal: true, selectedReasons: []})
  }

  render() {
    const {
      billingUnits,
      rootTeam,
      load,
      showSuccessModal,
      showAddBUModal,
      teamRegion,
      showTeamContainsUsersModal,
      showDeleteConfirmationModal,
      showReasonsModal,
      selectedReasons,
      showSuccessfullyDeletedModal,
      deleteSubmit,
      country,
      showToastBanner
    } = this.state
    const {formatMessage} = this.props
    const showAddBUButton = authentication.apps("admin.billingUnit")
    const business = authentication.business()

    return (
      <>
        <GenericModal
          title={formatMessage({id: "admin.containers.protectedTeams.list.requestModalSuccess.title"})}
          show={showSuccessModal}
          proceedButtonText={formatMessage({id: "admin.containers.protectedTeams.list.requestModalSuccess.back"})}
          onConfirm={() => this.toggleSuccessModal(false)}
          onHide={() => this.toggleSuccessModal(false)}>
          <Body large maxWidth="100%" marginBottom={0}>
            {formatMessage({id: "admin.containers.protectedTeams.list.requestModalSuccess.para1"})}
          </Body>
          <Body large maxWidth="100%" marginBottom={1}>
            {formatMessage({id: "admin.containers.protectedTeams.list.requestModalSuccess.para2"})}
          </Body>
        </GenericModal>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center">
            <H1 marginBottom={1} marginRight={3}>
              {formatMessage({id: "admin.containers.protectedTeams.list.countries.title"})}
            </H1>
            {!!billingUnits.length && (
              <Pill
                text={formatMessage(
                  {
                    id: "admin.containers.protectedTeams.list.businessUnits.pill"
                  },
                  {total: billingUnits.length}
                )}
              />
            )}
          </Flex>
          {showAddBUButton && (
            <Button id="adminSection_addCountry_button" primary onClick={this.showAddBU}>
              <FormattedMessage id="admin.containers.protectedTeams.list.countries.button" />
            </Button>
          )}
        </Flex>
        <Body small marginBottom={4}>
          <Body small marginBottom={1}>
            {formatMessage({id: "admin.containers.protectedTeams.list.countries.paragraph"})}
          </Body>
          <Body small>{formatMessage({id: "admin.containers.protectedTeams.list.businessUnits.para2"})}</Body>
        </Body>
        {showToastBanner && (
          <ToastBanner
            onDismiss={() => this.setState({showToastBanner: false})}
            text={formatMessage({id: "admin.countries.deleteCountry.toastBanner"}, {country})}
          />
        )}
        <Loading load={load}>
          <Flex width="100%" direction="column">
            <Flex paddingBottom={1}>
              <FirstCol sm={3}>
                <Label large weight="bold">
                  {formatMessage({id: "admin.containers.protectedTeams.list.businessUnits.cols.logo"})}
                </Label>
              </FirstCol>
              <Col sm={3}>
                <Label large weight="bold">
                  {formatMessage({id: "admin.containers.protectedTeams.list.businessUnits.cols.name"})}
                </Label>
              </Col>
              <Col sm={3}>
                <Label large weight="bold">
                  {formatMessage({id: "admin.containers.protectedTeams.list.businessUnits.cols.users"})}
                </Label>
              </Col>
              <EndCol sm={3} />
            </Flex>
            {billingUnits.map((team) => (
              <TeamListItem
                key={team.uuid}
                team={team}
                rootTeam={rootTeam}
                onSelectTeam={() => this.setSelectedTeam(team.uuid)}
                onDeleteTeam={this.deleteTeam}
              />
            ))}
          </Flex>
        </Loading>
        <GenericModal
          confirmButtonId="adminSection_country_modal_confirm_button"
          show={showAddBUModal}
          onHide={() => this.setState({showAddBUModal: false})}
          onConfirm={this.createBillingUnit}
          title={formatMessage({id: "admin.containers.protectedTeams.list.countries.button"})}>
          <Row marginBottom={2}>
            <BURegion
              id="addCountry"
              onChange={this.addBUonChange}
              value={teamRegion}
              businessId={business.uuid}
              label={formatMessage({id: "admin.containers.protectedTeams.list.countries.addModal.label"})}
            />
          </Row>
        </GenericModal>
        <GenericModal
          critical
          show={showTeamContainsUsersModal}
          onHide={() => this.setState({showTeamContainsUsersModal: false})}
          title={formatMessage({id: "admin.countries.deleteCountry.modals.teamContainsUsers.title"})}
          actions={
            <Flex>
              <Button id="adminSection_usersInTeam_ok_button" primary onClick={() => this.setState({showTeamContainsUsersModal: false})}>
                <FormattedMessage id="admin.countries.deleteCountry.modals.teamContainsUsers.button" />
              </Button>
            </Flex>
          }>
          <Body marginBottom={3}>
            <FormattedMessage id="admin.countries.deleteCountry.modals.teamContainsUsers.body" />
          </Body>
        </GenericModal>
        <GenericModal
          critical
          show={showDeleteConfirmationModal}
          onHide={() => this.setState({showDeleteConfirmationModal: false})}
          title={formatMessage({id: "admin.countries.deleteCountry.modals.confirmation.title"}, {country})}
          actions={
            <Flex>
              <Button id="adminSection_deleteCountry_submit_button" primary onClick={this.handleDeleteTeam} load={deleteSubmit}>
                <FormattedMessage id="admin.countries.deleteCountry.modals.confirmation.submit" />
              </Button>
              <Button marginLeft onClick={() => this.setState({showDeleteConfirmationModal: false})}>
                <FormattedMessage id="admin.countries.deleteCountry.modals.confirmation.cancel" />
              </Button>
            </Flex>
          }>
          <Body marginBottom={3}>
            <FormattedMessage id="admin.countries.deleteCountry.modals.confirmation.body" />
          </Body>
        </GenericModal>
        <GenericModal
          customWidth={760}
          show={showReasonsModal}
          onHide={() => this.setState({showReasonsModal: false})}
          title={formatMessage({id: "admin.countries.deleteCountry.modals.reasons.title"})}
          actions={
            <Flex>
              <Button id="adminSection_deleteCountryFeedBack_submit_button" primary onClick={this.handleSubmitReasons}>
                <FormattedMessage id="admin.countries.deleteCountry.modals.reasons.button" />
              </Button>
            </Flex>
          }>
          <Body marginBottom={4}>
            <FormattedMessage id="admin.countries.deleteCountry.modals.reasons.body" />
          </Body>
          <Flex wrap="wrap" margin={[1, 0]}>
            {/* eslint-disable-next-line no-undef */}
            {REASONS.map((reason: ReasonsInterface) => (
              <Selector
                name={formatMessage({id: reason.key})}
                onClick={this.handlePillClick}
                selected={selectedReasons.includes(reason.reason)}
              />
            ))}
          </Flex>
        </GenericModal>
        <GenericModal
          show={showSuccessfullyDeletedModal}
          onHide={() => this.setState({showSuccessfullyDeletedModal: false})}
          title={formatMessage({id: "admin.countries.deleteCountry.modals.success.title"})}
          actions={
            <Flex>
              <Button
                id="adminSection_deleteCountryFeedBack_okay_button"
                primary
                onClick={() => this.setState({showSuccessfullyDeletedModal: false})}>
                <FormattedMessage id="admin.countries.deleteCountry.modals.success.button" />
              </Button>
            </Flex>
          }
        />
      </>
    )
  }
}

export default injectIntl(withAuth(List))
